/**
 * Swappable Text-to-Speech (TTS) Service
 * 
 * Default implementation: Browser SpeechSynthesis API (zero cost, zero keys, instantaneous).
 * Designed with a clean interface so alternative providers (ElevenLabs, Azure, Kokoro, OpenAI TTS)
 * can be plugged in by swapping or extending this service.
 */

class BrowserTTSEngine {
  constructor() {
    this.synth = typeof window !== "undefined" && "speechSynthesis" in window ? window.speechSynthesis : null;
    this.currentUtterance = null;
    this.voices = [];
    this.isMuted = false;
    this.preferredVoice = null;

    if (this.synth) {
      this.loadVoices();
      if (typeof window !== "undefined" && window.speechSynthesis.onvoiceschanged !== undefined) {
        window.speechSynthesis.onvoiceschanged = () => this.loadVoices();
      }
    }
  }

  isSupported() {
    return !!this.synth;
  }

  loadVoices() {
    if (!this.synth) return;
    this.voices = this.synth.getVoices() || [];
    // Prioritize natural English voices
    const preferred =
      this.voices.find((v) => v.lang.startsWith("en") && (v.name.includes("Natural") || v.name.includes("Google") || v.name.includes("Daniel") || v.name.includes("Guy") || v.name.includes("David"))) ||
      this.voices.find((v) => v.lang.startsWith("en")) ||
      this.voices[0];
    this.preferredVoice = preferred || null;
  }

  setMuted(muted) {
    this.isMuted = muted;
    if (muted && this.synth) {
      this.synth.cancel();
    }
  }

  /**
   * Speak the given text
   * @param {string} text 
   * @param {object} callbacks { onStart, onBoundary, onEnd, onError }
   */
  speak(text, callbacks = {}) {
    if (!this.synth) {
      if (callbacks.onError) callbacks.onError(new Error("Speech synthesis not supported"));
      return;
    }

    this.stop();

    if (this.isMuted) {
      // Simulate silent caption playback if user has muted audio
      this.simulateSilentPlayback(text, callbacks);
      return;
    }

    const utterance = new SpeechSynthesisUtterance(text);
    this.currentUtterance = utterance;

    if (this.preferredVoice) {
      utterance.voice = this.preferredVoice;
    }

    utterance.rate = 1.0;
    utterance.pitch = 1.0;

    utterance.onstart = () => {
      if (callbacks.onStart) callbacks.onStart({ text });
    };

    utterance.onboundary = (event) => {
      // charIndex gives the current character index in text
      const charIndex = event.charIndex || 0;
      const charLength = event.charLength || 5;
      const currentWord = text.substring(charIndex, charIndex + charLength).trim();

      if (callbacks.onBoundary) {
        callbacks.onBoundary({
          charIndex,
          charLength,
          currentWord,
          name: event.name,
          elapsedTime: event.elapsedTime,
        });
      }
    };

    utterance.onend = () => {
      this.currentUtterance = null;
      if (callbacks.onEnd) callbacks.onEnd();
    };

    utterance.onerror = (e) => {
      this.currentUtterance = null;
      if (callbacks.onError) callbacks.onError(e);
    };

    this.synth.speak(utterance);
  }

  stop() {
    if (this.synth) {
      this.synth.cancel();
    }
    this.currentUtterance = null;
  }

  pause() {
    if (this.synth) {
      this.synth.pause();
    }
  }

  resume() {
    if (this.synth) {
      this.synth.resume();
    }
  }

  simulateSilentPlayback(text, callbacks) {
    if (callbacks.onStart) callbacks.onStart({ text });
    const words = text.split(/\s+/);
    let index = 0;
    const intervalMs = 280; // approximate words per minute

    const timer = setInterval(() => {
      if (index >= words.length) {
        clearInterval(timer);
        if (callbacks.onEnd) callbacks.onEnd();
        return;
      }
      const word = words[index];
      if (callbacks.onBoundary) {
        callbacks.onBoundary({
          charIndex: index,
          charLength: word.length,
          currentWord: word,
          elapsedTime: index * intervalMs,
        });
      }
      index++;
    }, intervalMs);

    this.stop = () => {
      clearInterval(timer);
      if (callbacks.onEnd) callbacks.onEnd();
    };
  }
}

// Export singleton instance conforming to TTS interface
export const ttsService = new BrowserTTSEngine();
export default ttsService;
